import { useState } from 'react';
import { toast } from 'sonner';

import { useAddCampaignItems, type CampaignItemInput } from './queries';

import { humanErrorMessage } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { pluralRu } from '@/lib/plural-ru';

const ITEM_FORMS = { one: 'элемент', few: 'элемента', many: 'элементов' };
const LINE_FORMS = { one: 'строка', few: 'строки', many: 'строк' };
const ERROR_FORMS = { one: 'ошибка', few: 'ошибки', many: 'ошибок' };

export interface CampaignItemsAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
}

interface ParsedLine {
  raw: string;
  item?: CampaignItemInput;
  error?: string;
}

// Right-split parser: trailing 1-3 integer tokens are appid[|qty|prio]; the
// leftover (possibly containing extra `|`) is the marketHashName. Steam market
// item names commonly include `|` (e.g. "AK-47 | Redline (Field-Tested)"),
// which would break a naive left-split.
const INT_RE = /^-?\d+$/;
const parseLine = (raw: string): ParsedLine => {
  const trimmed = raw.trim();
  if (!trimmed) return { raw };
  const parts = trimmed.split('|').map((p) => p.trim());
  if (parts.length < 2) {
    return { raw, error: 'ожидается `marketHashName|appid[|quantity|priority]`' };
  }

  let numericTail = 0;
  for (let i = parts.length - 1; i >= 0 && numericTail < 3; i--) {
    const token = parts[i];
    if (token !== undefined && INT_RE.test(token)) {
      numericTail++;
    } else {
      break;
    }
  }
  if (numericTail < 1) {
    return { raw, error: 'последний токен должен быть appid (целое число)' };
  }

  const nameParts = parts.slice(0, parts.length - numericTail);
  const marketHashName = nameParts.join(' | ').trim();
  if (!marketHashName) {
    return { raw, error: 'пустое marketHashName' };
  }

  const nums = parts.slice(parts.length - numericTail).map(Number);
  const [appidN, qtyN, prioN] = nums;
  if (appidN === undefined || !Number.isFinite(appidN) || appidN <= 0) {
    return { raw, error: 'appid должен быть положительным числом' };
  }

  const item: CampaignItemInput = { marketHashName, appid: appidN };
  if (qtyN !== undefined) {
    if (!Number.isFinite(qtyN) || qtyN <= 0) {
      return { raw, error: 'quantity должен быть положительным числом' };
    }
    item.quantity = qtyN;
  }
  if (prioN !== undefined) {
    if (!Number.isFinite(prioN)) {
      return { raw, error: 'priority должен быть числом' };
    }
    item.priority = prioN;
  }
  return { raw, item };
};

export const CampaignItemsAddDialog = ({
  open,
  onOpenChange,
  campaignId,
}: CampaignItemsAddDialogProps) => {
  const [text, setText] = useState('');
  const addItems = useAddCampaignItems(campaignId);

  const parsedLines = text
    .split('\n')
    .map(parseLine)
    .filter((p) => p.raw.length > 0 || p.item);

  const validItems = parsedLines.filter((p): p is ParsedLine & { item: CampaignItemInput } =>
    Boolean(p.item),
  );
  const errors = parsedLines.filter((p) => p.error);

  const handleSubmit = (): void => {
    if (validItems.length === 0) {
      toast.error('Добавьте хотя бы один валидный элемент');
      return;
    }
    addItems.mutate(
      validItems.map((p) => p.item),
      {
        onSuccess: () => {
          toast.success(`Добавлено ${pluralRu(validItems.length, ITEM_FORMS)}`);
          setText('');
          onOpenChange(false);
        },
        onError: (err) => toast.error(humanErrorMessage(err)),
      },
    );
  };

  const handleOpenChange = (next: boolean): void => {
    onOpenChange(next);
    if (!next) setText('');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Добавить элементы</DialogTitle>
          <DialogDescription>
            По одному на строку: <code>marketHashName|appid[|quantity|priority]</code>. Разделитель
            — `|`.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Элементы</Label>
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            rows={8}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder={
              'AK-47 | Redline (Field-Tested)|730|1\nStatTrak™ M4A4 | Asiimov (Battle-Scarred)|730|1|10'
            }
          />
        </div>
        <div className="text-xs text-muted-foreground">
          Валидных {pluralRu(validItems.length, LINE_FORMS)}
          {errors.length > 0 ? (
            <span className="ml-2 text-destructive">· {pluralRu(errors.length, ERROR_FORMS)}</span>
          ) : null}
        </div>
        {errors.length > 0 ? (
          <ul className="max-h-32 overflow-auto text-xs text-destructive">
            {errors.map((p, idx) => (
              <li key={idx}>
                «{p.raw}» — {p.error}
              </li>
            ))}
          </ul>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={addItems.isPending || validItems.length === 0}>
            {addItems.isPending ? 'Добавление…' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
